import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
const API_BASE = String(import.meta.env.VITE_API_BASE ||
    import.meta.env.VITE_API_URL ||
    "https://api.avaaio.com").replace(/\/+$/, "");
const EMPTY_STATE = {
    authorized: false,
    role: "",
    selectedMode: "Unknown",
    effectiveMode: "Unknown",
    offline: false,
    localSpeechRecognition: false,
    browserVoiceAvailable: false,
};
function normalizeRole(payload) {
    const user = payload?.user && typeof payload.user === "object"
        ? payload.user
        : payload;
    return String(user?.role ??
        payload?.role ??
        "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
}
function isAuthorizedAdmin(role) {
    return ["admin", "superadmin", "owner"].includes(role);
}
function readModeValue(payload, type) {
    const nested = payload?.connection_mode && typeof payload.connection_mode === "object"
        ? payload.connection_mode
        : {};
    const value = type === "selected"
        ? payload?.selected_mode ??
            payload?.selected ??
            payload?.mode ??
            nested?.selected_mode ??
            nested?.selected ??
            nested?.mode
        : payload?.effective_mode ??
            payload?.effective ??
            payload?.resolved_mode ??
            nested?.effective_mode ??
            nested?.effective ??
            nested?.resolved_mode ??
            payload?.selected_mode ??
            payload?.selected ??
            payload?.mode;
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
    if (normalized === "offline" ||
        normalized === "local" ||
        normalized === "offline_local" ||
        normalized === "local_offline") {
        return "Offline Local";
    }
    if (normalized === "online" ||
        normalized === "cloud" ||
        normalized === "connected") {
        return "Online";
    }
    return value ? String(value) : "Unknown";
}
function readLocalSpeechRecognition(payload) {
    const capabilities = payload?.capabilities && typeof payload.capabilities === "object"
        ? payload.capabilities
        : {};
    return Boolean(capabilities?.on_device_speech_recognition ??
        capabilities?.local_speech_recognition ??
        capabilities?.offline_stt ??
        payload?.on_device_speech_recognition ??
        payload?.local_speech_recognition ??
        false);
}
export default function ConnectionModeAdminDiagnostics() {
    const [state, setState] = useState(EMPTY_STATE);
    const authorizationHeaders = useMemo(() => {
        const token = localStorage.getItem("token") ||
            localStorage.getItem("access_token") ||
            "";
        return token
            ? {
                Authorization: `Bearer ${token}`,
            }
            : {};
    }, []);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const authResponse = await fetch(`${API_BASE}/api/v1/auth/me`, {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                        ...authorizationHeaders,
                    },
                });
                if (!authResponse.ok) {
                    if (!cancelled) {
                        setState(EMPTY_STATE);
                    }
                    return;
                }
                const authPayload = await authResponse.json();
                const role = normalizeRole(authPayload);
                if (!isAuthorizedAdmin(role)) {
                    if (!cancelled) {
                        setState(EMPTY_STATE);
                    }
                    return;
                }
                const modeResponse = await fetch(`${API_BASE}/api/v1/auth/connection-mode`, {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                        ...authorizationHeaders,
                    },
                });
                const modePayload = modeResponse.ok
                    ? await modeResponse.json()
                    : {};
                const selectedMode = readModeValue(modePayload, "selected");
                const effectiveMode = readModeValue(modePayload, "effective");
                if (!cancelled) {
                    setState({
                        authorized: true,
                        role,
                        selectedMode,
                        effectiveMode,
                        offline: effectiveMode === "Offline Local",
                        localSpeechRecognition: readLocalSpeechRecognition(modePayload),
                        browserVoiceAvailable: typeof window !== "undefined" &&
                            "speechSynthesis" in window,
                    });
                }
            }
            catch {
                if (!cancelled) {
                    setState(EMPTY_STATE);
                }
            }
        };
        void load();
        const interval = window.setInterval(() => {
            void load();
        }, 5000);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    }, [authorizationHeaders]);
    if (!state.authorized) {
        return null;
    }
    const blocked = state.offline ? "Blocked" : "Available";
    const rows = [
        ["Backend-verified role", state.role],
        ["Selected mode", state.selectedMode],
        ["Effective mode", state.effectiveMode],
        ["Cloud providers", blocked],
        ["Live tools", blocked],
        ["External integrations", blocked],
        ["Cloud speech recognition", blocked],
        ["Cloud voice output", blocked],
        [
            "Typed local chat",
            state.offline ? "Available" : "Available",
        ],
        [
            "On-device speech recognition",
            state.localSpeechRecognition
                ? "Available"
                : "Not installed",
        ],
        [
            "Browser local voice output",
            state.browserVoiceAvailable
                ? "Available when supported"
                : "Unavailable",
        ],
    ];
    return (_jsxs("section", { "aria-label": "Administrator Connection Mode diagnostics", style: {
            marginTop: 18,
            padding: "17px",
            borderRadius: 16,
            border: "1px solid rgba(255, 190, 78, 0.32)",
            background: "rgba(34, 22, 5, 0.54)",
        }, children: [_jsx("div", { style: {
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#ffd88d",
                }, children: "AVAAIO Control System" }), _jsx("div", { style: {
                    marginTop: 6,
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#fff8e8",
                }, children: "Administrator diagnostics" }), _jsx("div", { style: {
                    display: "grid",
                    gap: 7,
                    marginTop: 13,
                }, children: rows.map(([label, value]) => (_jsxs("div", { style: {
                        display: "grid",
                        gridTemplateColumns: "minmax(145px, 1fr) minmax(110px, auto)",
                        gap: 12,
                        alignItems: "start",
                        paddingBottom: 7,
                        borderBottom: "1px solid rgba(255, 220, 150, 0.10)",
                    }, children: [_jsx("span", { style: {
                                fontSize: 12,
                                lineHeight: 1.4,
                                color: "rgba(255, 242, 211, 0.70)",
                            }, children: label }), _jsx("span", { style: {
                                fontSize: 12,
                                lineHeight: 1.4,
                                fontWeight: 800,
                                textAlign: "right",
                                color: "#fff8e8",
                                overflowWrap: "anywhere",
                            }, children: value })] }, label))) }), _jsx("p", { style: {
                    margin: "12px 0 0",
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: "rgba(255, 242, 211, 0.60)",
                }, children: "This technical panel is rendered only after the AVAAIO backend confirms an authorized administrative account role." })] }));
}
